import React, { useState, useEffect, useRef } from 'react';
import { Smile } from 'lucide-react';
import ReactionsPanel from './ReactionsPanel';

export default function ReactionButton({
  onTriggerEffect,
  onSendReaction,
  onRaiseHand,
  onToggleBRB,
  isHandRaised = false,
  isBRB = false,
  reactionsEnabled = true,
  boostedReactionsEnabled = true,
  allowRaiseHand = true,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Close panel when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      // Check if click is inside the button container
      if (containerRef.current && containerRef.current.contains(e.target)) {
        return;
      }
      // Check if click is inside the reactions panel (rendered via portal)
      if (e.target.closest('.reactions-panel-portal')) {
        return;
      }
      setIsOpen(false);
    };

    // Add listener with slight delay to prevent immediate close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`p-3.5 md:p-2.5 rounded-lg transition-colors ${
          isOpen
            ? 'bg-[#6a1fbf]/20 text-[#6a1fbf]'
            : 'hover:bg-white/10'
        }`}
        title="Reactions"
      >
        <Smile className="w-7 h-7 md:w-4 md:h-4 text-white" />
      </button>

      {isOpen && (
        <ReactionsPanel
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onTriggerEffect={(effectId) => {
            onTriggerEffect?.(effectId);
            setIsOpen(false);
          }}
          onSendReaction={(id, emoji, boosted, points) => {
            onSendReaction?.(id, emoji, boosted, points);
            setIsOpen(false);
          }}
          onRaiseHand={() => {
            onRaiseHand?.();
            setIsOpen(false);
          }}
          onToggleBRB={() => {
            onToggleBRB?.();
            setIsOpen(false);
          }}
          isHandRaised={isHandRaised}
          isBRB={isBRB}
          reactionsEnabled={reactionsEnabled}
          boostedReactionsEnabled={boostedReactionsEnabled}
          allowRaiseHand={allowRaiseHand}
        />
      )}

      {/* Status indicator dots */}
      {(isHandRaised || isBRB) && (
        <div className="absolute -top-1 -right-1 flex gap-0.5">
          {isHandRaised && (
            <div className="w-2.5 h-2.5 bg-yellow-500 rounded-full border border-[#001233]" />
          )}
          {isBRB && (
            <div className="w-2.5 h-2.5 bg-orange-500 rounded-full border border-[#001233]" />
          )}
        </div>
      )}
    </div>
  );
}
